import LoginUtils from "@/utils/login/LoginUtils.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import TrueFalseNotSpecified from "@/utils/constants/TrueFalseNotSpecified.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import TrueFalseNotSpecifiedUtils from "@/utils/common/TrueFalseNotSpecifiedUtils.ts";
import YesNoDefault from "@/utils/constants/YesNoDefault.ts";
import DTOUser = namaTypes.DTOUser;
import DTOUserStandardSecurityLine = namaTypes.DTOUserStandardSecurityLine;
import DTOSecurityFieldAuthority = namaTypes.DTOSecurityFieldAuthority;

export default class CurrentUserUtils
{
  static fetchMinSearchQueryLength(entityType: string) {
    let userInfo = LoginUtils.currentUserInfo();
    if (!userInfo)
      return -1;
    let minLength = this.minEntitySearchQueryLength(userInfo, entityType);
    if (minLength)
      return minLength;
    for (let user of userInfo.alternativeUsers ?? []) {
      let alternativeMinLength = this.minEntitySearchQueryLength(user, entityType);
      if (alternativeMinLength)
        return alternativeMinLength;
    }
    return -1;
  }

  static minEntitySearchQueryLength(userInfo: DTOUser, entityType: string) {
    if (!userInfo.fullProfile || !userInfo.fullProfile.standardLines || userInfo.fullProfile.fullAuthority)
      return -1;
    if (entityType == null)
      return -1;
    let minSearchQueryLength = this.findMinSearchQueryLength(entityType
      , userInfo.settings && userInfo.settings.standardLines ? userInfo.settings.standardLines : []);
    if (minSearchQueryLength)
      return minSearchQueryLength;
    return this.findMinSearchQueryLength(entityType, userInfo.fullProfile.standardLines);
  }

  static findMinSearchQueryLength(entityType: string, standardLines: DTOUserStandardSecurityLine[]) {
    if (!standardLines)
      return -1;
    let linesWithMinSearchQueryLength = standardLines.filter(l => l.minSearchQueryLength);
    if (!linesWithMinSearchQueryLength || !linesWithMinSearchQueryLength.length)
      return -1;
    let firstLineMatched = linesWithMinSearchQueryLength.find(l => l.targetEntity, entityType);

    if (firstLineMatched)
      return firstLineMatched.minSearchQueryLength;
    firstLineMatched = linesWithMinSearchQueryLength.find(l => this.entityTypeListContainsEntity(l.targetEntities, entityType));
    if (firstLineMatched)
      return firstLineMatched.minSearchQueryLength;
    firstLineMatched = linesWithMinSearchQueryLength.find(l => !l.targetEntities && !l.targetEntity);
    if (firstLineMatched)
      return firstLineMatched.minSearchQueryLength;
    return -1;
  }

  static entityTypeListContainsEntity(targetEntities: namaTypes.EntityReferenceData | undefined, entityType: string): boolean {
    return targetEntities && targetEntities["lines.entityType"] && (targetEntities["lines.entityType"] as string).includes("," + entityType + ",");
  }

  static userCanNOTDeleteLine(entityType: string, fieldId: string, applicableWhen: string) {
    if (!entityType)
      return true;
    if (!ClientCache.isEntityEnabled(entityType))
      return true;
    return this.userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen, l => !!l.preventDeleteLine);
  }

  static userCanNOTInsertLine(entityType: string, fieldId: string, applicableWhen: string) {
    if (!entityType)
      return true;
    if (!ClientCache.isEntityEnabled(entityType))
      return true;
    return this.userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen, l => !!l.preventInsertLine);
  }

  static userCanNotCopyLine(entityType: string, fieldId: string, applicableWhen: string) {
    if (!entityType)
      return true;
    if (!ClientCache.isEntityEnabled(entityType))
      return true;
    return this.userCanNotOnALineConsiderAlternative(entityType, fieldId, applicableWhen, l => !!l.preventCopyLine);
  }

  static userCanNotOnALineConsiderAlternative(targetEntity: string, fieldId: string, applicableWhen: string
    , fetcher: (authority: DTOSecurityFieldAuthority) => boolean) {
    let currentUserInfo = LoginUtils.currentUserInfo()!;
    let userCanNotOnALine: TrueFalseNotSpecified = this.userCanNotOnALine(currentUserInfo, targetEntity, fieldId, applicableWhen, fetcher);
    if (TrueFalseNotSpecifiedUtils.isSpecified(userCanNotOnALine))
      return TrueFalseNotSpecifiedUtils.isTrue(userCanNotOnALine);
    let alternativeUsers = currentUserInfo?.alternativeUsers;
    if (alternativeUsers && alternativeUsers?.length)
      for (let user of alternativeUsers) {
        let userCanNotOnALine1: TrueFalseNotSpecified = this.userCanNotOnALine(user, targetEntity, fieldId, applicableWhen, fetcher);
        if (TrueFalseNotSpecifiedUtils.isSpecified(userCanNotOnALine1))
          return TrueFalseNotSpecifiedUtils.isTrue(userCanNotOnALine);
      }
    return false;
  }

  static userCanNotOnALine(currentUserInfo: DTOUser, targetEntity: string, fieldId: string, applicableWhen: string
    , fetcher: (authority: DTOSecurityFieldAuthority) => boolean) {
    if (!currentUserInfo.fullProfile)
      return TrueFalseNotSpecified.NotSpecified;
    if (!targetEntity || !fieldId)
      return TrueFalseNotSpecified.False;
    if (currentUserInfo.fullProfile.fullAuthority)
      return TrueFalseNotSpecified.False;
    if (currentUserInfo.settings?.disabledFields && !currentUserInfo.fullProfile.disabledFields)
      return TrueFalseNotSpecified.NotSpecified;
    let authority = SecurityUtils.getFieldSecurityLine(currentUserInfo, targetEntity, fieldId, applicableWhen);
    if (!authority)
      return TrueFalseNotSpecified.NotSpecified;
    return TrueFalseNotSpecifiedUtils.fromBoolean(fetcher(authority));
  }

  static defaultPageSize(): string | undefined {
    let currentUser: DTOUser | undefined = LoginUtils.loginInfo?.userInfo;
    if (currentUser && currentUser.settings && currentUser.settings.defaultPageSize)
      return currentUser.settings.defaultPageSize;
    return;
  }

  static defaultSearchPageSize(): string | undefined {
    let currentUser: DTOUser | undefined = LoginUtils.loginInfo?.userInfo;
    if (currentUser && currentUser.settings && currentUser.settings.defaultSearchPageSize)
      return currentUser.settings.defaultSearchPageSize;
    return;
  }

  static calcDefaultPageSize(defaultPageSize: string | undefined): number {
    switch (defaultPageSize) {
      case "TwentyFive":
        return 25;
      case "Fifty":
        return 50;
      case "SeventyFive":
        return 75;
      case "OneHundred":
        return 100;
      case "All":
        return 50000;
    }
    return 25;
  }

  static userCanUseAI() {
    const allowAccessToAI = LoginUtils.loginInfo?.userInfo?.settings?.allowAccessToAI;
    if (allowAccessToAI === YesNoDefault.Yes)
      return true;
    if (allowAccessToAI === YesNoDefault.No)
      return false;
    const profileAllowAccessToAi = LoginUtils.loginInfo?.userInfo?.fullProfile?.allowAccessToAI;
    return profileAllowAccessToAi === YesNoDefault.Yes;
  }

  static userCanUseChat() {
    return !(LoginUtils.loginInfo?.userInfo?.settings?.preventAccessToChat || LoginUtils.loginInfo?.userInfo?.fullProfile?.preventAccessToChat);
  }
}
